import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  getCurrentPositions,
  getPortfolioEvolution,
  listBenchmarks,
  getBenchmarkEvolution,
  type CurrentPosition,
  type EvolutionPoint,
  type Benchmark,
  type BenchmarkEvolutionPoint,
} from "../api/client";
import { format } from "date-fns";
import DateRangeSelector from "../components/DateRangeSelector";
import { type DateRangeKey, getDateRangeStart } from "../utils/dateRange";

interface Props {
  portfolioId: number | null;
}

const fmtUSD = (v?: number | null) =>
  v == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);

const BENCHMARK_COLORS = ["#a855f7", "#f97316", "#ec4899", "#14b8a6"];

export default function DashboardPage({ portfolioId }: Props) {
  const navigate = useNavigate();
  const [positions, setPositions] = useState<CurrentPosition[]>([]);
  const [evolution, setEvolution] = useState<EvolutionPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangeKey>("3M");
  const [benchmarks, setBenchmarks] = useState<Benchmark[]>([]);
  const [selectedBenchmarkIds, setSelectedBenchmarkIds] = useState<Set<number>>(new Set());
  const [benchmarkData, setBenchmarkData] = useState<Record<number, BenchmarkEvolutionPoint[]>>({});

  useEffect(() => {
    if (!portfolioId) return;
    setLoading(true);
    Promise.all([
      getCurrentPositions(portfolioId),
      getPortfolioEvolution(portfolioId),
      listBenchmarks(portfolioId),
    ])
      .then(([posRes, evoRes, bmRes]) => {
        setPositions(posRes.data);
        setEvolution(evoRes.data);
        setBenchmarks(bmRes.data);
        setSelectedBenchmarkIds(new Set(bmRes.data.map((bm: Benchmark) => bm.id)));
      })
      .finally(() => setLoading(false));
  }, [portfolioId]);

  useEffect(() => {
    if (!portfolioId) return;
    selectedBenchmarkIds.forEach((bmId) => {
      if (!benchmarkData[bmId]) {
        getBenchmarkEvolution(portfolioId, bmId)
          .then((res) => setBenchmarkData((prev) => ({ ...prev, [bmId]: res.data })))
          .catch(() => {/* ignore */});
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBenchmarkIds, portfolioId]);

  const toggleBenchmark = (id: number) => {
    setSelectedBenchmarkIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!portfolioId) {
    return (
      <div style={{ textAlign: "center", padding: "80px 0", color: "#64748b" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📈</div>
        <h2 style={{ color: "#1e293b" }}>Welcome to TradingWeb</h2>
        <p>Select or create a portfolio in the sidebar to get started.</p>
      </div>
    );
  }

  if (loading) return <p>Loading dashboard…</p>;

  const totalValue = positions.reduce((s, p) => s + (p.market_value ?? 0), 0);
  const totalUnrealized = positions.reduce((s, p) => s + (p.unrealized_pnl ?? 0), 0);
  const totalRealized = positions.reduce((s, p) => s + (p.realized_pnl ?? 0), 0);

  const latestEvo = evolution[evolution.length - 1];
  const prevEvo = evolution[evolution.length - 2];
  const dayChange = latestEvo && prevEvo ? latestEvo.total_value - prevEvo.total_value : null;

  // Evolution chart data
  const startDate = getDateRangeStart(dateRange);
  const filteredEvo = startDate ? evolution.filter((d) => d.date >= startDate) : evolution;
  const bmIndexByDate: Record<number, Record<string, number>> = {};
  selectedBenchmarkIds.forEach((bmId) => {
    bmIndexByDate[bmId] = {};
    (benchmarkData[bmId] ?? []).forEach((pt) => {
      bmIndexByDate[bmId][pt.date] = pt.benchmark_value;
    });
  });
  const chartData = filteredEvo.map((d) => {
    const row: Record<string, unknown> = {
      ...d,
      date: format(new Date(d.date), "MMM d, yy"),
      total_pnl: Number(d.unrealized_pnl) + Number(d.realized_pnl),
    };
    selectedBenchmarkIds.forEach((bmId) => {
      const val = bmIndexByDate[bmId]?.[d.date];
      if (val !== undefined) row[`bm_${bmId}`] = val;
    });
    return row;
  });

  return (
    <div>
      <h2 style={{ margin: "0 0 20px", color: "#1e293b" }}>Dashboard</h2>

      {/* Summary cards */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 28 }}>
        <KpiCard title="Portfolio Value" value={fmtUSD(totalValue)} sub={dayChange != null ? `${dayChange >= 0 ? "+" : ""}${fmtUSD(dayChange)} today` : undefined} color="#3b82f6" />
        <KpiCard title="Unrealized P&L" value={fmtUSD(totalUnrealized)} color={totalUnrealized >= 0 ? "#10b981" : "#ef4444"} />
        <KpiCard title="Realized P&L" value={fmtUSD(totalRealized)} color={totalRealized >= 0 ? "#10b981" : "#ef4444"} />
        <KpiCard title="Positions" value={String(positions.length)} color="#8b5cf6" />
        <KpiCard
          title="Data as of"
          value={latestEvo ? format(new Date(latestEvo.date), "MMM d, yyyy") : "N/A"}
          color="#64748b"
        />
      </div>

      {/* Top positions */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20 }}>
        <h3 style={{ margin: "0 0 12px", color: "#334155" }}>Top Positions by Value</h3>
        {positions.length === 0 ? (
          <p style={{ color: "#94a3b8" }}>No positions yet. Import a Flex Query to populate.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                {["Symbol", "Market Value", "Unrealized P&L", "% of Portfolio"].map((h) => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: "#64748b", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...positions]
                .sort((a, b) => (b.market_value ?? 0) - (a.market_value ?? 0))
                .slice(0, 10)
                .map((p) => (
                  <tr key={p.symbol} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "8px 12px", fontWeight: 700 }}>
                      <button
                        onClick={() => navigate(`/position-history?symbol=${p.symbol}`)}
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          color: "#3b82f6",
                          fontWeight: 700,
                          fontSize: 14,
                          textDecoration: "underline",
                          textDecorationStyle: "dotted",
                          fontFamily: "inherit",
                        }}
                      >
                        {p.symbol}
                      </button>
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "right" }}>{fmtUSD(p.market_value)}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", color: (p.unrealized_pnl ?? 0) >= 0 ? "#16a34a" : "#dc2626" }}>
                      {fmtUSD(p.unrealized_pnl)}
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "right" }}>
                      {totalValue > 0 ? `${(((p.market_value ?? 0) / totalValue) * 100).toFixed(1)}%` : "—"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Evolution Charts */}
      {evolution.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0, color: "#1e293b" }}>Portfolio Evolution</h3>
            <DateRangeSelector value={dateRange} onChange={setDateRange} />
            {benchmarks.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "#64748b" }}>Benchmarks:</span>
                {benchmarks.map((bm, i) => (
                  <button
                    key={bm.id}
                    onClick={() => toggleBenchmark(bm.id)}
                    style={{
                      padding: "3px 10px",
                      borderRadius: 12,
                      border: `1.5px solid ${BENCHMARK_COLORS[i % BENCHMARK_COLORS.length]}`,
                      background: selectedBenchmarkIds.has(bm.id)
                        ? BENCHMARK_COLORS[i % BENCHMARK_COLORS.length]
                        : "transparent",
                      color: selectedBenchmarkIds.has(bm.id) ? "#fff" : BENCHMARK_COLORS[i % BENCHMARK_COLORS.length],
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {bm.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20 }}>
            <ResponsiveContainer width="100%" height={360}>
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={fmtUSD} tick={{ fontSize: 12 }} width={90} domain={["auto", "auto"]} />
                <Tooltip formatter={(value: number | undefined) => value != null ? fmtUSD(value) : "—"} />
                <Legend />
                <Line type="monotone" dataKey="total_value" stroke="#3b82f6" strokeWidth={2} dot={false} name="Portfolio Value" />
                {[...selectedBenchmarkIds].map((bmId, i) => {
                  const bm = benchmarks.find((b) => b.id === bmId);
                  return (
                    <Line
                      key={bmId}
                      type="monotone"
                      dataKey={`bm_${bmId}`}
                      stroke={BENCHMARK_COLORS[i % BENCHMARK_COLORS.length]}
                      strokeWidth={2}
                      strokeDasharray="5 3"
                      dot={false}
                      name={bm?.name ?? `Benchmark ${bmId}`}
                      connectNulls
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20, marginTop: 20 }}>
            <h3 style={{ margin: "0 0 16px", color: "#1e293b" }}>P&amp;L Breakdown</h3>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={fmtUSD} tick={{ fontSize: 12 }} width={90} domain={["auto", "auto"]} />
                <Tooltip formatter={(value: number | undefined) => value != null ? fmtUSD(value) : "—"} />
                <Legend />
                <Area type="monotone" dataKey="realized_pnl" stackId="pnl" stroke="#f59e0b" fill="#fef3c7" strokeWidth={1.5} dot={false} name="Realized P&L" />
                <Area type="monotone" dataKey="unrealized_pnl" stackId="pnl" stroke="#10b981" fill="#d1fae5" strokeWidth={1.5} dot={false} name="Unrealized P&L" />
                <Line type="monotone" dataKey="total_pnl" stroke="#3b82f6" strokeWidth={2.5} dot={false} name="Total P&L" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ title, value, sub, color }: { title: string; value: string; sub?: string; color: string }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 20px", minWidth: 160, borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}
